import { useEffect, useState, useCallback, useRef } from 'react';
import type { ButtonHTMLAttributes, MouseEvent } from 'react';
import { useKeyPressEvent } from 'react-use';
import { useTranslations } from 'next-intl';
import type { DateRange } from 'react-day-picker';
import { format } from 'date-fns/format';
import { Ui, Theme, String } from '@silvertours/front-shared';
import { useLocale } from '../Runtime';
import { addTimeToDate } from './DatePicker/Calendar';
import { ButtonWrapper } from './DatePicker/Button.styles';
import { Backdrop, StyledCalendar, TimeRange } from './DateRangePicker.styles';
import { Button } from './DatePicker';
import { TimePicker } from './TimePicker';
import {
  trackEditDatesClicked,
  trackEditTimeClicked,
  trackSelectDates,
  trackSelectTime,
} from '../analytics/gtm/events';
import {
  trackDateRangeConfirmSelectionClick,
  trackEditTimeClick,
  trackEditTravelDateClick,
} from '../analytics/generic';

const HOUR_IN_MS = 1000 * 60 * 60;

type DateRangePickerProps = {
  open?: boolean;
  vertical?: boolean;
  size?: 'small' | 'default';
  dateDisplay?: 'default' | 'short';
  error?: string;
  invalid?: boolean;
  onDatesChange?: (dateRange: DateRange) => void;
} & DateRange &
  Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>;

const DateRangePicker = ({
  from,
  to,
  open = false,
  vertical = false,
  size = 'default',
  dateDisplay = 'default',
  error,
  invalid,
  className,
  onDatesChange,
}: DateRangePickerProps) => {
  const t = useTranslations('entities.datePicker');
  const { language } = useLocale();
  const buttonWrapperRef = useRef<HTMLDivElement>(null);

  const [isCalendarVisible, setIsCalendarVisible] = useState(open);
  const [selectedDates, setSelectedDates] = useState<DateRange>({
    from,
    to,
  });

  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();

  const closeCalendar = useCallback(() => {
    setIsCalendarVisible(false);
    buttonWrapperRef.current?.querySelector('button')?.focus();
  }, []);

  useEffect(() => {
    setSelectedDates({ from, to });
  }, [from, to]);

  useKeyPressEvent('Escape', () => {
    if (isCalendarVisible) {
      closeCalendar();
    }
  });

  // Handle a `:focus-within`-like behavior for the button wrapper,
  // close the calendar when focus moves outside of the button wrapper.
  useEffect(() => {
    buttonWrapperRef.current?.addEventListener(
      'focusout',
      // Using `any` here instead of `FocusEvent` to avoid type issues
      // with `relatedTarget` and DIV elements which cannot be focused.
      (event: any) => {
        const self = event.currentTarget;
        if (self?.contains(event.relatedTarget) === false) {
          setIsCalendarVisible(false);
        }
      },
    );
  }, [buttonWrapperRef]);

  const localDateFormats =
    String.dateFormats[language as keyof typeof String.dateFormats];

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    closeCalendar();
  };

  const handleDatesChange = (
    dateRange: DateRange,
    shouldClose: boolean = true,
  ) => {
    if (typeof onDatesChange !== 'undefined') {
      onDatesChange(dateRange);
    }

    if (shouldClose) {
      setIsCalendarVisible(isMobileTouchDevice);
    }

    trackSelectDates();
    trackDateRangeConfirmSelectionClick(dateRange.from, dateRange.to);
  };

  const handleTimeChange = (time: string, type: 'from' | 'to') => {
    const [hours, minutes] = time.split(':');
    if (selectedDates[type]) {
      const dateWithTime = addTimeToDate(
        selectedDates[type],
        Number(hours),
        Number(minutes),
      );

      const newDateRange = { ...selectedDates, [type]: dateWithTime };
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange(newDateRange);
      }
      setSelectedDates(newDateRange);
      trackSelectTime();
    }
  };

  const handleButtonClick = useCallback(() => {
    trackEditTravelDateClick();
    trackEditDatesClicked();
    setIsCalendarVisible(true);
  }, []);

  const getDays = () => {
    if (selectedDates.from && selectedDates.to) {
      // Inspired by https://youmightnotneed.com/date-fns#differenceInDays,
      // because differenceInHours() from date-fns always rounds which is not what we want
      const days =
        ((selectedDates.to as any) - (selectedDates.from as any)) /
        HOUR_IN_MS /
        24;
      return Number.isInteger(days) ? days : Math.ceil(days);
    }
    return 1;
  };

  return (
    <>
      {isCalendarVisible && <Backdrop onClick={handleBackdropClick} />}

      <ButtonWrapper className={className} ref={buttonWrapperRef}>
        <Button
          title={t('datePicker.label')}
          from={from}
          to={to}
          size={size}
          dateDisplay={dateDisplay}
          onClick={handleButtonClick}
          aria-invalid={error || invalid ? 'true' : undefined}
          aria-errormessage={error || invalid ? 'date-error' : undefined}
          invalid={!!error || invalid}
        />

        {error && <Ui.ErrorMessage id="date-error">{error}</Ui.ErrorMessage>}

        {isCalendarVisible && (
          <StyledCalendar
            from={from}
            to={to}
            dayCount={getDays()}
            closeText={t('close')}
            vertical={vertical}
            showHeader={vertical}
            showFooter={vertical}
            onChange={handleDatesChange}
            onClose={() => {
              closeCalendar();
            }}
          />
        )}
      </ButtonWrapper>

      <TimeRange>
        <TimePicker
          id="depTime"
          type="from"
          label={t('depTime.label')}
          value={
            selectedDates.from
              ? format(selectedDates.from, localDateFormats.timeGeneric)
              : undefined
          }
          onClick={() => {
            trackEditTimeClick();
            trackEditTimeClicked();
          }}
          onChange={handleTimeChange}
          invalid={!!error || invalid}
          aria-invalid={error || invalid ? 'true' : undefined}
          aria-errormessage={!!error || invalid ? 'date-error' : undefined}
        />
        <TimePicker
          id="destTime"
          type="to"
          label={t('destTime.label')}
          value={
            selectedDates.to
              ? format(selectedDates.to, localDateFormats.timeGeneric)
              : undefined
          }
          onClick={() => {
            trackEditTimeClick();
            trackEditTimeClicked();
          }}
          onChange={handleTimeChange}
          invalid={!!error || invalid}
          aria-invalid={error || invalid ? 'true' : undefined}
          aria-errormessage={!!error || invalid ? 'date-error' : undefined}
        />
      </TimeRange>
    </>
  );
};

export { DateRangePicker };
